import type { Document } from "@/api/models"
import type { CitationPreviewState } from "@/features/chat/contexts/citation-preview-shared"

import {
  getCitationFilePath,
  getCitationOriginalFilePath,
  getLibraryOriginalFilePath,
  isOpaqueFilthCitation,
  isUnresolvedFilthCitation,
} from "@/api/api"
import { getCitationType } from "@/features/chat/components/citation.utils"
import { SettingsService } from "@/services/settings-service"
import { getCitationAnchorPage, getCitationTextAnchor } from "@/utils/citation-anchor-utils"
import {
  findLibraryDocumentForCitation,
  formatCitationDisplay,
  formatM365Citation,
  parseCitationParts,
} from "@/utils/citation-utils"
import { extractCleanFilename, getFileExtension } from "@/utils/file-utils"
import { normalizeForMatching } from "@/utils/normalize-for-matching"

/** File extensions whose built-in viewers are approximate — not fully faithful to the original. */
const APPROXIMATE_VIEWER_EXTENSIONS = new Set(["docx", "xlsx", "xls"])

/**
 * File extensions whose original bytes we render faithfully. A Context v1
 * citation carries no source media type — FILTH's contract exposes one only on
 * the original response itself — so the display filename is the signal, and
 * anything unrecognized keeps the extracted-text preview.
 */
const ORIGINAL_VIEWER_EXTENSIONS = new Set(["pdf"])

/** True when the cited file's own name says we can render its original bytes. */
function hasFaithfulOriginalViewer(originalName: string | undefined): boolean {
  if (!originalName?.trim()) {
    return false
  }
  return ORIGINAL_VIEWER_EXTENSIONS.has(getFileExtension(originalName.trim()).toLowerCase())
}

function resolveSnippets(
  quote: string | null,
  chunkText: string | null
): { quoteSnippet: string; chunkSnippet: string } {
  const quoteSnippet = quote?.trim() ? normalizeForMatching(quote) : ""
  const chunkSnippet =
    chunkText?.trim() && chunkText !== quote ? normalizeForMatching(chunkText) : ""
  return { quoteSnippet, chunkSnippet }
}

function resolveCitationHeaderTitle(
  citation: string,
  librariesAndDocuments?: [string, Document][]
): string {
  const citationType = getCitationType(citation)
  const cleanCitation = citation.split("#:~:text=")[0]

  if (citationType === "web") {
    try {
      const url = new URL(cleanCitation)
      return url.hostname
    } catch {
      return cleanCitation
    }
  }

  if (citationType === "m365") {
    const { displayName } = formatM365Citation(citation)
    return displayName
  }

  const { fileNameWithoutPage } = formatCitationDisplay(citation, librariesAndDocuments)
  const colonIndex = fileNameWithoutPage.indexOf(": ")
  return colonIndex !== -1
    ? fileNameWithoutPage.substring(colonIndex + 2).trim()
    : fileNameWithoutPage
}

export type ResolvedCitationPreview = {
  state: CitationPreviewState
  title: string
  downloadFileName: string | undefined
  downloadKind?: "extracted" | "original"
  sourceRevision?: string
}

function extractedTextFileName(originalName: string): string {
  const baseName = originalName.replace(/\\/g, "/").split("/").pop()?.trim() || "document"
  const safeName = baseName.replace(/[\\/:*?"<>|]/g, "_")
  const lastDot = safeName.lastIndexOf(".")
  const stem = lastDot > 0 ? safeName.slice(0, lastDot) : safeName
  return `${stem || "document"}.extracted.txt`
}

export function resolveCitationPreview(
  citation: string,
  citationSection: string,
  options?: { chunkText?: string; documents?: [string, Document][] }
): ResolvedCitationPreview {
  const parsed = parseCitationParts(citation)
  const source = parsed.source
  const chunkText = options?.chunkText ?? null
  const { quoteSnippet, chunkSnippet } = resolveSnippets(parsed.quote, chunkText)
  const docs = options?.documents
  const citationTitle = resolveCitationHeaderTitle(citation, docs)

  const libDoc = findLibraryDocumentForCitation(citation, docs)
  const page = getCitationAnchorPage(libDoc) ?? parsed.page
  const pageFragment = page != null ? `#page=${page}` : ""
  const textAnchor = getCitationTextAnchor(libDoc)

  // Decided before any URL is built, and it wins outright. A citation naming a
  // FILTH-backed library that no delivered document answers to has no
  // trustworthy locator: `getCitationFilePath` would still hand back a generic
  // library URL for a handle-shaped citation, and requesting it asks the backend
  // for a token FILTH never issued. Return a state the pane can name instead.
  if (isUnresolvedFilthCitation(citation, libDoc, docs)) {
    return {
      state: {
        citation,
        source,
        page,
        quote: parsed.quote,
        documentUrl: "",
        originalDocumentUrl: null,
        downloadUrl: "",
        quoteSnippet,
        chunkSnippet,
        chunkText,
        showModifiedPreviewBanner: null,
        isUnresolvedSource: true,
      },
      title: citationTitle,
      downloadFileName: undefined,
    }
  }

  const documentAwareIndexedUrl = getCitationFilePath(source, citationSection, libDoc)
  const isOpaqueDocumentUrl = isOpaqueFilthCitation(source, libDoc)
  const rawRevision = libDoc?.extra_data?.filth_revision_sequence
  const sourceRevision =
    isOpaqueDocumentUrl &&
    typeof rawRevision === "string" &&
    /^(0|[1-9]\d{0,18})$/.test(rawRevision)
      ? rawRevision
      : undefined
  const indexedUrl = documentAwareIndexedUrl + pageFragment
  const filthOriginalUrl = getCitationOriginalFilePath(source, citationSection, libDoc)
  let unmodifiedUrlWithPage: string | null = null
  if (
    !isOpaqueDocumentUrl &&
    libDoc?.original_sourcefile?.trim() &&
    libDoc.sourcefile &&
    citationSection &&
    getCitationType(citation) === "library"
  ) {
    const libraryId = source.split(":")[0]
    if (libraryId) {
      const unmodifiedBase = getLibraryOriginalFilePath(
        citationSection,
        libraryId,
        libDoc.sourcefile,
        libDoc.original_sourcefile.trim()
      )
      unmodifiedUrlWithPage = unmodifiedBase ? unmodifiedBase + pageFragment : null
    }
  }

  const libraryIdForFlag = source.split(":")[0]
  let useUnmodifiedPreviews = false
  try {
    const indexes = SettingsService.getInstance().settings.indexes
    useUnmodifiedPreviews =
      indexes.find((i) => i.id === libraryIdForFlag)?.use_unmodified_previews === true
  } catch {
    useUnmodifiedPreviews = false
  }

  const viewerUrl =
    unmodifiedUrlWithPage && useUnmodifiedPreviews ? unmodifiedUrlWithPage : indexedUrl
  // Preview the cited file itself when we can render it faithfully. The
  // extracted text stays as the fallback the viewer falls back to, so an
  // original the backend cannot serve (404/503) still shows something.
  const originalDocumentUrl =
    filthOriginalUrl && hasFaithfulOriginalViewer(libDoc?.original_sourcefile ?? citationTitle)
      ? filthOriginalUrl + pageFragment
      : null
  const downloadUrl = filthOriginalUrl ?? unmodifiedUrlWithPage ?? indexedUrl
  let downloadFileName: string | undefined
  if (filthOriginalUrl) {
    downloadFileName = libDoc?.original_sourcefile?.trim() || undefined
  } else if (isOpaqueDocumentUrl) {
    downloadFileName = extractedTextFileName(citationTitle)
  } else if (unmodifiedUrlWithPage && libDoc?.original_sourcefile?.trim()) {
    downloadFileName = libDoc.original_sourcefile.trim()
  }
  let downloadKind: ResolvedCitationPreview["downloadKind"]
  if (filthOriginalUrl) {
    downloadKind = "original"
  } else if (isOpaqueDocumentUrl) {
    downloadKind = "extracted"
  }

  const isConvertedFile = Boolean(unmodifiedUrlWithPage && !useUnmodifiedPreviews)
  const viewedExtension = getFileExtension(extractCleanFilename(viewerUrl)).toLowerCase()
  const isApproximateViewer = APPROXIMATE_VIEWER_EXTENSIONS.has(viewedExtension)
  let showModifiedPreviewBanner: CitationPreviewState["showModifiedPreviewBanner"] = null
  if (isOpaqueDocumentUrl) {
    showModifiedPreviewBanner = "extracted"
  } else if (isConvertedFile) {
    showModifiedPreviewBanner = "converted"
  } else if (isApproximateViewer) {
    showModifiedPreviewBanner = "approximate"
  }

  return {
    state: {
      citation,
      source,
      page,
      quote: parsed.quote,
      textAnchor: isOpaqueDocumentUrl ? textAnchor : null,
      documentUrl: viewerUrl,
      originalDocumentUrl,
      downloadUrl,
      quoteSnippet,
      chunkSnippet,
      chunkText,
      showModifiedPreviewBanner,
      isUnresolvedSource: false,
    },
    title: citationTitle,
    sourceRevision,
    downloadFileName,
    downloadKind,
  }
}
